import { useCallback, useEffect, useRef, useState } from 'react';

type Animation = 'fade' | 'float-up' | 'fade-image';

interface UseScrollRevealOptions {
  animation?: Animation;
  delay?: number;
  threshold?: number;
  rootMargin?: string;
}

const DURATION_MS: Record<Animation, number> = {
  'fade': 700,
  'float-up': 850,
  'fade-image': 1200,
};

const TRANSITION_PROPERTY: Record<Animation, string> = {
  'fade': 'opacity',
  'float-up': 'opacity, transform',
  'fade-image': 'opacity',
};

function getHiddenStyle(animation: Animation): React.CSSProperties {
  return {
    opacity: 0,
    transitionProperty: TRANSITION_PROPERTY[animation],
    transitionDuration: `${DURATION_MS[animation]}ms`,
    transitionTimingFunction: 'ease-in',
    ...(animation === 'float-up' && { transform: 'translateY(1rem)' }),
  };
}

function getVisibleStyle(animation: Animation): React.CSSProperties {
  return {
    opacity: 1,
    transitionProperty: TRANSITION_PROPERTY[animation],
    transitionDuration: `${DURATION_MS[animation]}ms`,
    transitionTimingFunction: 'ease-in',
    ...(animation === 'float-up' && { transform: 'translateY(0)' }),
  };
}

export function useScrollReveal({
  animation = 'float-up',
  delay = 0,
  threshold = 0.15,
  rootMargin = '0px 0px -60px 0px',
}: UseScrollRevealOptions = {}) {
  const [revealed, setRevealed] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Check reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ref = useCallback(
    (node: HTMLElement | null) => {
      // Cleanup previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      elementRef.current = node;

      if (!node || prefersReducedMotion) return;

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (delay > 0) {
              setTimeout(() => setRevealed(true), delay);
            } else {
              setRevealed(true);
            }
            observerRef.current?.disconnect();
            observerRef.current = null;
          }
        },
        { threshold, rootMargin },
      );

      observerRef.current.observe(node);
    },
    [delay, threshold, rootMargin, prefersReducedMotion],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  if (prefersReducedMotion) {
    return { ref: () => {}, style: { opacity: 1 } as React.CSSProperties };
  }

  return {
    ref,
    style: revealed ? getVisibleStyle(animation) : getHiddenStyle(animation),
  };
}
